import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Lock, Mail } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { login, forgotPassword } from "../api/authApi";
import { useAuthStore } from "@/stores/authStore";
import type { User } from "@/shared/types";

export default function Login() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);

  const setAuth = useAuthStore((state) => state.setAuth);
  const clearAuth = useAuthStore((state) => state.clearAuth);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data = await login({ email, password });
      
      // Backend now returns accessToken (refresh token is in HttpOnly cookie)
      const accessToken = data.accessToken || data.token; // Support both for backward compatibility
      
      if (!data.success || !accessToken || !data.user) {
        throw new Error(data.message || "Login failed - invalid response");
      }

      // Cast to full User type (backend returns full User object, type definition is incomplete)
      const user = data.user as User;
      
      // Ensure we have a valid user object with required fields
      // Super admins have tenantId: null, which is valid
      if (!user.id || (user.role !== "super_admin" && !user.tenantId)) {
        throw new Error("Invalid user data received from server");
      }
      
      // Store access token in memory only (refresh token is in HttpOnly cookie)
      setAuth(accessToken, user);
      
      // Also set in React Query cache for immediate availability
      queryClient.setQueryData<User>(["/auth/user"], data.user as User);
      
      // Navigate immediately - don't wait for API verification
      // Dashboard will handle user verification if needed
      let redirectPath = "/";
      if (user.role === "super_admin") {
        redirectPath = "/super-admin/dashboard";
      } else if (user.role === "staff") {
        redirectPath = "/staff-dashboard";
      }
      setLocation(redirectPath);
    } catch (error: any) {
      // Clear auth state on error
      clearAuth();
      queryClient.setQueryData<User | null>(["/auth/user"], null);
      
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: error.message || "Invalid email or password",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      toast({
        variant: "destructive",
        title: "Email Required",
        description: "Please enter your email address",
      });
      return;
    }

    setIsSendingReset(true);

    try {
      const data = await forgotPassword(forgotPasswordEmail);
      
      if (data.success) {
        toast({
          title: "Password Reset Request",
          description: data.message || "If an account exists with this email, a password reset link has been sent.",
        });
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
      } else {
        // Show error toast for non-existent email
        toast({
          variant: "destructive",
          title: "Account Not Found",
          description: data.message || "No account found with this email address.",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to Send Reset Link",
        description: error.message || "Please try again later",
      });
    } finally {
      setIsSendingReset(false);
    }
  };


  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 text-white relative overflow-hidden">
      {/* Decorative background orbs */}
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl opacity-30 animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-indigo-400 rounded-full blur-3xl opacity-20"></div>

      {/* Header */}
      <header className="flex items-center justify-between px-8 py-6 z-10">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center p-1.5">
            <img src="/images/logo.png" alt="TradeReady" className="h-full w-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="heading-app-name">
            TradeReady
          </h1>
        </div>
        <p className="text-sm text-blue-100 hidden sm:block">Trade-Focused. Built for Growth.</p>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-md rounded-3xl p-8 sm:p-10 shadow-2xl border border-white/20"
          data-testid="card-login"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-3 text-white" data-testid="heading-welcome">
              Welcome Back
            </h2>
            <p className="text-blue-100 text-sm">
              Sign in to manage jobs, quotes, and invoices — all in one place.
            </p>
          </div>

          {/* Password Login Form */}
          <form onSubmit={handlePasswordLogin} className="space-y-5" data-testid="form-password-login">
            <div>
              <Label htmlFor="email" className="block text-sm font-medium mb-2 text-blue-100">
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="you@business.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 w-full bg-white/90 text-gray-800 border-gray-200 focus:ring-2 focus:ring-blue-400 dark:bg-white/90 dark:text-gray-800"
                  required
                  disabled={isSubmitting}
                  data-testid="input-email"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="block text-sm font-medium text-blue-100">
                  Password
                </Label>
              
              </div>
              <PasswordInput
                id="password"
                name="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/90 text-gray-800 border-gray-200 focus:ring-2 focus:ring-blue-400 dark:bg-white/90 dark:text-gray-800"
                required
                disabled={isSubmitting}
                data-testid="input-password"
                leftIcon={<Lock className="h-4 w-4 text-gray-400" />}
              />
              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordEmail(email);
                    setShowForgotPassword(true);
                  }}
                  className="text-sm font-medium text-blue-100 hover:text-white transition-colors duration-200 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-600 rounded px-2 py-1 -mr-2"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold shadow-lg"
              disabled={isSubmitting}
              data-testid="button-password-login"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        </motion.div>
      </main>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md bg-white text-gray-900 border-0 shadow-xl">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-2xl font-bold text-gray-900">Forgot Password?</DialogTitle>
            <DialogDescription className="text-gray-600">
              No worries! Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-password-email" className="text-sm font-medium text-gray-700">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="forgot-password-email"
                  type="email"
                  placeholder="you@business.com"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="pl-10 w-full"
                  required
                  disabled={isSendingReset}
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail("");
                }}
                disabled={isSendingReset}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSendingReset}
                className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white"
              >
                {isSendingReset ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* Footer */}
      <footer className="py-6 text-center text-sm text-blue-100 z-10">
        <p>© {new Date().getFullYear()} TradeReady — Built for Trades, Designed for Growth.</p>
        <p className="mt-2 space-x-3">
          <Link href="/legal/terms" data-testid="footer-link-terms">
            <span className="underline hover:text-white cursor-pointer">Terms</span>
          </Link>
          <span>•</span>
          <Link href="/legal/privacy" data-testid="footer-link-privacy">
            <span className="underline hover:text-white cursor-pointer">Privacy</span>
          </Link>
          <span>•</span>
          <Link href="/legal/dpa" data-testid="footer-link-dpa">
            <span className="underline hover:text-white cursor-pointer">DPA</span>
          </Link>
          <span>•</span>
          <Link href="/legal/sla" data-testid="footer-link-sla">
            <span className="underline hover:text-white cursor-pointer">SLA</span>
          </Link>
        </p>
      </footer>
    </div>
  );
}

